import { format } from 'date-fns';
import { hyphenateSync } from 'hyphen/en';
import { convertNumberToDesiredFormat } from 'helpers/transformHelpers';

export const EMPTY_TEXT = '---';

export const displayText = (text: string | null | undefined): string => {
  if (text == null || text === '') return EMPTY_TEXT;
  return text;
};

/**
 * Checks if a character is a delimiter (- or space).
 */
const isDelimiter = (char: string): boolean => {
  return char === '-' || char === ' ';
};

/**
 * Splits text by delimiters (- and spaces), keeping the delimiters as separate parts.
 */
const splitTextWithDelimiters = (text: string): string[] => {
  const parts: string[] = [];
  let currentPart = '';

  // eslint-disable-next-line no-restricted-syntax
  for (const char of text) {
    if (isDelimiter(char)) {
      if (currentPart) {
        parts.push(currentPart);
        currentPart = '';
      }
      parts.push(char);
    } else {
      currentPart += char;
    }
  }
  if (currentPart) {
    parts.push(currentPart);
  }

  return parts;
};

/**
 * Groups parts with smart line breaks based on maxLineLength.
 */
const groupPartsWithLineBreaks = (
  parts: string[],
  maxLineLength: number
): string => {
  let result = '';
  let currentLine = '';

  // eslint-disable-next-line no-restricted-syntax
  for (const part of parts) {
    if (isDelimiter(part)) {
      currentLine += part;
    } else {
      const combinedLength = currentLine.length + part.length;
      const shouldStartNewLine = combinedLength >= maxLineLength;

      if (shouldStartNewLine) {
        if (currentLine) {
          result += `${currentLine}\n`;
        }
        currentLine = part;
      } else {
        currentLine += part;
      }
    }
  }

  if (currentLine) {
    result += currentLine;
  }

  return result;
};

export const splitTextByMaxLineLength = (
  text: string,
  maxLineLength: number
): string => {
  if (maxLineLength <= 0) return text;

  const lines: string[] = [];
  for (let index = 0; index < text.length; index += maxLineLength) {
    lines.push(text.slice(index, index + maxLineLength));
  }

  return lines.join('\n');
};

/**
 * Formats text with smart line breaks based on delimiters (- and spaces).
 * Groups parts together if their combined length is less than 8 characters,
 * otherwise adds a newline before the current part.
 *
 * @param text - The text to format
 * @param maxLineLength - Maximum length before adding a line break (default: 8)
 * @returns Formatted text with line breaks
 */
export const formatTextWithSmartLineBreaks = (
  text: string,
  maxLineLength: number = 8
): string => {
  if (!text) return '';
  if (!text.includes('-') && !text.includes(' ')) {
    return splitTextByMaxLineLength(text, maxLineLength);
  }

  const parts = splitTextWithDelimiters(text);
  return groupPartsWithLineBreaks(parts, maxLineLength);
};

export const displayNumber = (
  number: number | null | undefined
): string | null => {
  if (number == null) return EMPTY_TEXT;
  return convertNumberToDesiredFormat(number, 'fixed');
};

export const displayDate = (
  dateStr: string | null | undefined,
  precision: 'day' | 'datetime' = 'day'
): string => {
  if (!dateStr) return EMPTY_TEXT;

  // For date-only strings (YYYY-MM-DD), append time to force local timezone interpretation
  // Otherwise, they're parsed as UTC which causes off-by-one day errors in certain timezones
  const dateOnlyPattern = /^\d{4}-\d{2}-\d{2}$/;
  const dateStrWithTime = dateOnlyPattern.test(dateStr)
    ? `${dateStr}T00:00:00`
    : dateStr;

  const date = new Date(dateStrWithTime);
  if (Number.isNaN(date.getTime())) return EMPTY_TEXT;
  if (precision === 'day') {
    return format(date, 'MMM dd, yyyy');
  }
  return format(date, 'MMM dd, yyyy hh:mma');
};

export const formatValueForWorkOrder = (value: string): string => {
  return value === EMPTY_TEXT ? '' : value;
};

export const hyphenateText = (word: string): string[] =>
  hyphenateSync(word).split('\u00AD');
